// Constantes del juego
export const BOARD_SIZE = 8
export const INIT_MOVES = 8
export const COMBO_SIZE = 6

// Shuffle array (Fisher-Yates)
export const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// Elegir elemento aleatorio de una lista
export const pickRandom = <T>(list: T[]): T => {
  return list[Math.floor(Math.random() * list.length)]
}

// Formatear números grandes (1000 -> 1k)
export const numFormat = (num: number, digits = 1): string => {
  const si = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
  ]
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/
  let i = si.length - 1
  for (i; i > 0; i--) {
    if (num >= si[i].value) break
  }
  return (num / si[i].value).toFixed(digits).replace(rx, '$1') + si[i].symbol
}

// Delay con blink effect
export const blinkDelay = (
  setBlink: (val: boolean) => void,
  onAfter?: () => void,
  time = 600
): void => {
  setBlink(true)
  setTimeout(() => {
    onAfter?.()
    setBlink(false)
  }, time)
}

// Obtener IDs adyacentes (arriba, abajo, izquierda, derecha)
export const getAdjacentIds = (id: number, size = BOARD_SIZE): number[] => {
  const row = Math.floor(id / size)
  const col = id % size
  const adjacent: number[] = []

  // Arriba
  if (row > 0) adjacent.push(id - size)
  // Abajo
  if (row < size - 1) adjacent.push(id + size)
  // Izquierda
  if (col > 0) adjacent.push(id - 1)
  // Derecha
  if (col < size - 1) adjacent.push(id + 1)

  return adjacent
}

// Obtener IDs adyacentes incluyendo diagonales
export const getAdjacentDiagIds = (id: number, size = BOARD_SIZE): number[] => {
  const row = Math.floor(id / size)
  const col = id % size
  const adjacent = getAdjacentIds(id, size)

  // Diagonal superior izquierda
  if (row > 0 && col > 0) adjacent.push(id - size - 1)
  // Diagonal superior derecha
  if (row > 0 && col < size - 1) adjacent.push(id - size + 1)
  // Diagonal inferior izquierda
  if (row < size - 1 && col > 0) adjacent.push(id + size - 1)
  // Diagonal inferior derecha
  if (row < size - 1 && col < size - 1) adjacent.push(id + size + 1)

  return adjacent
}

// Match recursivo de celdas adyacentes con el mismo icono
export const matchRecursiveAdjCells = (
  id: number,
  cells: { icon: string }[],
  size = BOARD_SIZE,
  withDiag = false,
  visited = new Set<number>()
): number[] => {
  if (visited.has(id)) return []
  visited.add(id)

  const currentIcon = cells[id]?.icon
  if (!currentIcon) return []

  const result = [id]
  const adjacentIds = withDiag ? getAdjacentDiagIds(id, size) : getAdjacentIds(id, size)

  for (const adjId of adjacentIds) {
    if (adjId >= 0 && adjId < cells.length && !visited.has(adjId)) {
      if (cells[adjId]?.icon === currentIcon) {
        result.push(...matchRecursiveAdjCells(adjId, cells, size, withDiag, visited))
      }
    }
  }

  return result
}

// Obtener matches únicos
export const getUniqueMatches = (
  id: number,
  cells: { icon: string }[],
  size = BOARD_SIZE,
  withDiag = false
): number[] => {
  const matches = matchRecursiveAdjCells(id, cells, size, withDiag)
  return [...new Set(matches)]
}

// Color de celda tipo ajedrez
export const getChessColor = (id: number, size = BOARD_SIZE): 'light' | 'dark' => {
  const row = Math.floor(id / size)
  return (row + id) % 2 === 0 ? 'light' : 'dark'
}

// Añadir a objeto contable
export const addToObj = (obj: Record<string, number>, key: string, amount = 1): void => {
  if (obj.hasOwnProperty(key)) {
    obj[key] += amount
  } else {
    obj[key] = amount
  }
}
